import { AxiosResponse } from 'axios';
import {
  ICreateSupplierRequest,
  ICreateSupplierResponse,
  IDeleteSupplierResponse,
  IDeleteSuppliersFilters,
  IGetSupplierByIdResponse,
  IGetSuppliersFilters,
  IGetSuppliersResponse,
  IMoveSupplierRequest,
  ISetSupplierRequest,
  IUpdateSupplierResponse,
  IGetSuppliersMetricsFilter,
  IGetSuppliersMetricsResponse,
  IUpdateSupplierRequest,
} from 'models/http/supplier';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from 'utils/http';
import { HTTP } from './core.http';
import DataUploadBaseHttp from './utils/data-upload.http';
import { IGetSimpleListFilters, IGetSimpleListResponse } from 'models/http/common';

class SupplierHttp extends DataUploadBaseHttp implements HTTP<IDeleteSuppliersFilters> {
  protected readonly route: DataUploadModels = ModelsNames.Suppliers;

  public async get(filters?: IGetSuppliersFilters, abortController?: AbortController): Promise<IGetSuppliersResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${queryParams.toString()}`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetSuppliersResponse>) => {
        return response.data;
      });
  }

  public async getSimple(filters?: IGetSimpleListFilters, abortController?: AbortController): Promise<IGetSimpleListResponse> {
    const queryParams = prepareQPFromFilters({ siteId: filters?.siteId });

    return serverAxios
      .get(`/${this.route}/simple?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSupplierByIdResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetSupplierByIdResponse>) => {
        return response.data;
      });
  }

  public async calcSupplierRank(abortController?: AbortController): Promise<void> { 
    return serverAxios
      .get(`/${this.route}/calcSupplierRank`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<void>) => {
        return response.data;
      });
  }

  public async getSuppliersMetrics(
    filters: IGetSuppliersMetricsFilter,
    abortController?: AbortController,
  ): Promise<IGetSuppliersMetricsResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}/metrics?${queryParams.toString()}`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetSuppliersMetricsResponse>) => {
        return response.data;
      });
  }

  public async create(supplier: ICreateSupplierRequest): Promise<ICreateSupplierResponse> {
    return serverAxios
      .post(`/${this.route}`, supplier, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreateSupplierResponse>) => {
        return response.data;
      });
  }

  public async set(id: number, supplier: ISetSupplierRequest): Promise<IUpdateSupplierResponse> {
    return serverAxios
      .put(`/${this.route}/${id}`, supplier, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateSupplierResponse>) => {
        return response.data;
      });
  }

  public async update(id: number, supplier: IUpdateSupplierRequest, abortController?: AbortController): Promise<IUpdateSupplierResponse> {
    return serverAxios
      .patch(`/${this.route}/${id}`, supplier, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IUpdateSupplierResponse>) => {
        return response.data;
      });
  }

  public async move(id: number, position: IMoveSupplierRequest): Promise<IUpdateSupplierResponse> {
    return serverAxios
      .patch(`/${this.route}/move/${id}`, position, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateSupplierResponse>) => {
        return response.data;
      });
  }

  public async delete(ids: number[]): Promise<IDeleteSupplierResponse>;
  public async delete(filters: IDeleteSuppliersFilters): Promise<IDeleteSupplierResponse>;
  public async delete(filters: number[] | IDeleteSuppliersFilters): Promise<IDeleteSupplierResponse> {
    return serverAxios
      .delete(`/${this.route}`, {
        data: prepareFiltersOfSelection(filters),
        timeout: HttpTimeoutPriority.Low,
      })
      .then();
  }
}

export default SupplierHttp;
